<script setup lang="ts">
const emit = defineEmits(["close"]);
const { trackEvent } = useGAnalytics();
const { toaster } = useToaster();
const { createAuthAttemptAndRedirect } = useAuthController();
const userStore = useUserStore();
const route = useRoute();
const showNotificationsMenu = ref(false);
const localePath = useLocalePath();

const doLogout = () => {
  trackEvent("dropdown_sign_out");
  if (route.path.includes("user-portal")) navigateTo(localePath("/"));
  userStore.logOut();
  toaster("success", "logoutSuccess");
  emit("close");
};

//const appStateStore = useAppStateStore();
//const currentMerchant = computed(() => appStateStore.currentMerchant);

const close = () => {
  emit("close");
};

const hasRole = (role: string) => userStore.roles?.includes(role) ?? false;
const displayInfluencerMenuForMarketing = useRuntimeConfig().public
  .allowInfluencerMenuForMarketing
  ? true
  : false;
const filteredMenuItems = computed(() =>
  menuItems.filter(
    (item) =>
      !item.requiresRole ||
      hasRole(item.requiresRole) ||
      (item.requiresRole === "influencer" && displayInfluencerMenuForMarketing),
  ),
);

const menuItems = [
  {
    name: "menuDropdown.dashboard",
    icon: "mdi:view-dashboard",
    path: "/user-portal/dashboard",
  },
  /*{
    name: "menuDropdown.collection",
    icon: "mdi:collections-bookmark",
    path: "/user-portal/collection",
  },*/
  {
    name: "menuDropdown.offers",
    icon: "mdi:swap-horizontal-circle",
    path: "/user-portal/offers",
  },
  {
    name: "menuDropdown.activity",
    icon: "mdi:clock-arrow",
    path: "/user-portal/activity",
  },
  {
    name: "menuDropdown.referrals",
    icon: "mdi:people-outline",
    path: "/user-portal/referrals",
    requiresRole: "influencer",
  },
  {
    name: "atelier.page.seoTitle",
    icon: "mdi:diamond-stone",
    path: "/user-portal/vip",
  },
  {
    name: "menuDropdown.settings",
    icon: "mdi:settings",
    path: "/user-portal/account",
  },
];

const loggedOutMenuItems = [
  {
    name: "menuDropdown.signIn",
    icon: "mdi:power",
    path: "signup",
  },
];

const hoveredIndex = ref<number>();
const setHoveredIndex = (index: number) => {
  hoveredIndex.value = index;
};
const clearHoveredIndex = () => {
  hoveredIndex.value = undefined;
};
</script>
<template>
  <div
    class="bg-black row-span-3 mb-4 md:mb-0 md:h-fit md:border md:border-secondary-500"
    :class="
      userStore.isLoggedIn
        ? 'overflow-y-scroll md:overflow-y-auto md:rounded-b-xl'
        : 'overflow-y-auto md:rounded-xl'
    "
  >
    <div v-if="userStore.isLoggedIn">
      <HeaderAccountNotificationsDropdownItem
        v-model:show-notifications="showNotificationsMenu"
      />
      <HeaderAccountNotificationsDropdown
        v-if="showNotificationsMenu"
        class="fixed top-0 -right-0 w-screen h-screen bg-neutral-800 flex-col shadow-sm shadow-black border-secondary-500 z-20"
        mode="mobile"
        @close="showNotificationsMenu = false"
        @close-parent-popover="emit('close')"
      />
      <div
        v-for="(item, index) in filteredMenuItems"
        :key="item.label ?? $t(`main.${item.name}`)"
        class="px-2 py-4 md:p-2 border-b border-secondary-500 md:border-0"
      >
        <NuxtLinkLocale
          v-if="userStore.isLoggedIn"
          :data-test="'link-user-menu-' + item.path"
          :to="item.path"
        >
          <div
            class="flex items-center gap-6 sm:gap-3 p-2 font-semibold hover:bg-neutral-500 hover:rounded-sm hover:text-primary-500 transition-all duration-300"
            :class="[
              index === hoveredIndex || $route.path?.includes(`${item.path}`)
                ? 'bg-neutral-500 text-primary-100 rounded-sm'
                : 'text-neutral-300',
            ]"
            @click="(close(), trackEvent(`dropdown_${item.path}`))"
            @mouseover="setHoveredIndex(index)"
            @mouseleave="clearHoveredIndex"
          >
            <NuxtIcon
              :name="item.icon"
              class="px-3 md:px-0 w-10 h-10 sm:w-8 sm:h-8"
            />
            <p class="text-xl md:text-base font-bold md:font-normal uppercase">
              {{ item.label ?? $t(`main.${item.name}`) }}
            </p>
          </div>
        </NuxtLinkLocale>
      </div>
      <div
        class="w-full h-20 md:h-auto border-b md:border-b-0 md:border-t border-secondary-500 px-2 py-4 md:p-2 md:rounded-b-xl"
        data-test="link-user-menu-logout"
        @click="doLogout"
      >
        <div
          class="p-2 flex items-center gap-6 sm:gap-3 text-primary-800 hover:text-neutral-200 cursor-pointer transition-all duration-300"
        >
          <NuxtIcon
            name="mdi:power"
            class="px-3 md:px-0 w-10 h-10 sm:w-8 sm:h-8"
          />
          <button
            class="text-xl font-bold md:font-normal md:text-base"
            data-test="button-dropdown-logout"
          >
            {{ $t("main.menuDropdown.signOut") }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-else
      v-for="(item, index) in loggedOutMenuItems"
      :key="item.name"
      class="px-2 py-4 md:p-2 first:border-t border-b border-secondary-500 md:border-0"
    >
      <div
        :data-test="'link-user-menu-' + item.path"
        @click="createAuthAttemptAndRedirect(item.path)"
      >
        <div
          class="flex items-center gap-4 p-2 font-semibold hover:bg-neutral-500 hover:rounded-sm transition-all duration-300 cursor-pointer"
          :class="[
            index === hoveredIndex || $route.path?.includes(`${item.path}`)
              ? 'bg-neutral-500 text-primary-500 rounded-sm'
              : 'text-neutral-300',
          ]"
          @click="(close(), trackEvent(`dropdown_${item.path}`))"
          @mouseover="setHoveredIndex(index)"
          @mouseleave="clearHoveredIndex"
        >
          <p class="px-3 text-xl md:text-base font-bold md:font-normal">
            {{ $t(`main.${item.name}`) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
